import { createContext, useContext, useState } from 'react';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { useTranslations } from './TranslationContext';
import { useContent } from './ContentContext';
import { useDraft } from './DraftContext';

const EditModeContext = createContext();

export function EditModeProvider({ children }) {
  const { isAdmin } = useIsAdmin();
  const { bulkUpdateTranslations } = useTranslations();
  const { saveContent, pendingContentChanges, updateContent } = useContent();
  const draft = useDraft();
  const [editMode, setEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleTranslationUpdate = (key, value, lang) => {
    // Forward to ContentContext which now writes to DraftContext
    updateContent(key, value, lang);
  };

  const handleSave = async () => {
    if (!draft.hasDraft && pendingContentChanges.length === 0) return;

    setIsSaving(true);
    try {
      // Save draft to the API — await it so the in-progress save is tracked
      // before bulkUpdateTranslations runs (prevents concurrent save races).
      await draft.saveDraft();
      // Also save translation-context changes (localStorage-based translations)
      if (pendingContentChanges.length > 0) {
        await bulkUpdateTranslations(pendingContentChanges);
      }
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleEditMode = () => {
    if (editMode && draft.hasDraft) {
      // Draft auto-saves, just exit edit mode
      draft.saveDraft();
    }
    setEditMode(prev => !prev);
  };

  // Combine content changes with draft-level changes (sections, appearance)
  // so the UI correctly flags "needs publish" for section visibility toggles.
  const hasUnsavedChanges = pendingContentChanges.length > 0 || draft.hasDraft || draft.isDirty;

  const value = {
    editMode,
    setEditMode,
    toggleEditMode,
    pendingChanges: pendingContentChanges, // backward compat
    hasUnsavedChanges,
    setPendingChanges: () => {},            // no-op for compat
    isSaving,
    handleTranslationUpdate,
    handleSave,
    isAdmin,
  };

  return (
    <EditModeContext.Provider value={value}>
      {children}
    </EditModeContext.Provider>
  );
}

export function useEditMode() {
  const context = useContext(EditModeContext);
  if (!context) {
    throw new Error('useEditMode must be used within an EditModeProvider');
  }
  return context;
}
